import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, Copy, Loader2, ArrowLeft } from "lucide-react";
import { api, fmtINR } from "../lib/api";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";

export default function Checkout() {
    const { items, clear } = useCart();
    const { customer, wallet, refresh } = useAuth();
    const navigate = useNavigate();
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!customer) {
            navigate("/login?next=/checkout", { replace: true });
            return;
        }
        if (items.length === 0) {
            navigate("/cart", { replace: true });
            return;
        }
        const body = {
            items: items.map((i) => ({
                reward_id: i.reward_id,
                denomination: i.denomination,
                points_to_use: i.points_to_use,
                qty: i.qty,
            })),
        };
        api.post("/api/cart/preview", body).then((r) => setPreview(r.data));
    }, [customer, items, navigate]);

    const placeOrder = async () => {
        setBusy(true);
        setErr("");
        try {
            const body = {
                items: items.map((i) => ({
                    reward_id: i.reward_id,
                    denomination: i.denomination,
                    points_to_use: i.points_to_use,
                    qty: i.qty,
                })),
            };
            const r = await api.post("/api/cart/checkout", body);
            if (r.data.razorpay_order) {
                openRazorpay(r.data);
            } else {
                // pure points checkout
                const v = await api.post("/api/cart/verify", { group_id: r.data.group_id });
                clear();
                await refresh();
                setResult(v.data);
            }
        } catch (e) {
            setErr(e.response?.data?.detail || "Checkout failed");
            setBusy(false);
        }
    };

    const openRazorpay = (order) => {
        const loadScript = () =>
            new Promise((resolve) => {
                if (window.Razorpay) return resolve();
                const s = document.createElement("script");
                s.src = "https://checkout.razorpay.com/v1/checkout.js";
                s.onload = resolve;
                document.body.appendChild(s);
            });
        loadScript().then(() => {
            const opts = {
                key: order.razorpay_key_id,
                amount: order.razorpay_order.amount,
                currency: order.razorpay_order.currency,
                order_id: order.razorpay_order.id,
                name: "Fundlexperiences",
                description: `Cart • ${items.length} item(s)`,
                theme: { color: "#CCFF00" },
                prefill: {
                    contact: customer?.mobile,
                    name: customer?.name || "",
                },
                handler: async (resp) => {
                    try {
                        const v = await api.post("/api/cart/verify", {
                            group_id: order.group_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        clear();
                        await refresh();
                        setResult(v.data);
                    } catch (e) {
                        setErr("Payment verification failed");
                    } finally {
                        setBusy(false);
                    }
                },
                modal: { ondismiss: () => setBusy(false) },
            };
            new window.Razorpay(opts).open();
        });
    };

    if (result) return <Success result={result} />;
    if (!preview) return <div className="p-10 text-center text-[#6b7280]">Loading checkout…</div>;

    return (
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-6">
            <Link to="/cart" className="text-xs text-[#6b7280] inline-flex items-center gap-1 hover:text-[#0b1437]">
                <ArrowLeft size={12} /> Back to cart
            </Link>
            <h1 className="font-display text-2xl md:text-3xl font-bold mt-2">Checkout</h1>

            <div className="grid md:grid-cols-3 gap-6 mt-5">
                <div className="md:col-span-2 space-y-4">
                    <div className="fx-card p-5">
                        <h2 className="font-display text-lg font-semibold mb-3">Account</h2>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <KV k="Name" v={customer?.name || "—"} />
                            <KV k="Mobile" v={`+${customer?.mobile}`} />
                            <KV k="Loyalty ID" v={customer?.loyalty_id} />
                            <KV k="Tier" v={customer?.tier} />
                        </div>
                    </div>
                    <div className="fx-card p-5">
                        <h2 className="font-display text-lg font-semibold mb-3">Items ({preview.items.length})</h2>
                        <div className="space-y-2 text-sm">
                            {preview.items.map((it) => (
                                <div key={`${it.reward_id}-${it.denomination}`} className="flex justify-between border-b border-[rgba(11,20,55,0.05)] pb-2 last:border-0">
                                    <div>
                                        <p className="font-semibold">{it.brand_name} × {it.qty}</p>
                                        <p className="text-[11px] text-[#6b7280]">{it.reward_name} • ₹{it.denomination}</p>
                                    </div>
                                    <div className="text-right">
                                        <p>{fmtINR(it.line_value_paise)}</p>
                                        {it.points_used > 0 && (
                                            <p className="text-[#c9a959] text-[11px]">
                                                − {it.points_used.toLocaleString("en-IN")} pts
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="fx-card p-4 flex items-center gap-3 text-sm">
                        <ShieldCheck size={18} className="text-[#c9a959]" />
                        <p className="text-[#4b5563]">
                            Payments are processed securely via Razorpay. Vouchers are issued instantly on success.
                        </p>
                    </div>
                </div>

                <aside className="fx-card p-5 self-start md:sticky md:top-24">
                    <h2 className="font-display text-lg font-semibold mb-3">Payment summary</h2>
                    <Row k="Cart value" v={fmtINR(preview.total_value_paise)} />
                    <Row k="Points discount" v={`− ${fmtINR(preview.total_points_used * 100 / 4)}`} pos />
                    <div className="border-t border-[rgba(11,20,55,0.10)] my-3" />
                    <Row k="Pay via Razorpay" v={fmtINR(preview.total_cash_paise)} strong />
                    <Row k="Points used" v={`${preview.total_points_used.toLocaleString("en-IN")} pts`} sub />
                    <Row
                        k="Wallet after"
                        v={`${(preview.wallet_points_left_after).toLocaleString("en-IN")} pts`}
                        sub
                    />
                    <button
                        onClick={placeOrder}
                        disabled={busy}
                        data-testid="place-order-btn"
                        className="fx-btn-primary w-full mt-4"
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                        {preview.total_cash_paise > 0
                            ? `Pay ${fmtINR(preview.total_cash_paise)}`
                            : "Place order"}
                    </button>
                    {err && <p className="text-red-400 text-sm mt-2">{err}</p>}
                </aside>
            </div>
        </div>
    );
}

function Row({ k, v, pos, strong, sub }) {
    return (
        <div className={`flex justify-between text-sm ${sub ? "text-[#6b7280] text-xs" : ""}`}>
            <span className="text-[#4b5563]">{k}</span>
            <span className={`${pos ? "text-[#c9a959]" : ""} ${strong ? "font-bold text-[#0b1437] text-base" : ""}`}>{v}</span>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-widest text-[#9ca3af]">{k}</p>
            <p className="text-[#0b1437]">{v}</p>
        </div>
    );
}

function Success({ result }) {
    const copyAll = () => {
        const text = result.redemptions
            .map((r) => `${r.brand_name} ₹${r.denomination}\nCode: ${r.voucher_code}\nPIN: ${r.voucher_pin}`)
            .join("\n\n");
        navigator.clipboard.writeText(text);
    };
    return (
        <div className="max-w-2xl mx-auto px-4 py-10">
            <div className="text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#c9a959]/15 text-[#c9a959] flex items-center justify-center fx-anim-pop">
                    <CheckCircle2 size={42} />
                </div>
                <h1 className="font-display text-3xl font-bold mt-4">Order placed!</h1>
                <p className="text-[#6b7280] mt-1">
                    {result.redemptions.length} voucher{result.redemptions.length > 1 ? "s" : ""} issued. Codes
                    delivered to your account.
                </p>
            </div>
            <div className="space-y-3 mt-7">
                {result.redemptions.map((r) => (
                    <div key={r.id} className="fx-card p-4">
                        <div className="flex items-center justify-between">
                            <p className="font-semibold">{r.brand_name} • ₹{r.denomination}</p>
                            <span className="fx-chip bg-[#c9a959]/15 text-[#c9a959] border border-[#c9a959]/30">
                                ISSUED
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <KV k="Voucher code" v={r.voucher_code} />
                            <KV k="PIN" v={r.voucher_pin} />
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex gap-3 mt-5">
                <button onClick={copyAll} className="fx-btn-ghost flex-1">
                    <Copy size={14} /> Copy all
                </button>
                <Link to="/account/vouchers" className="fx-btn-primary flex-1" data-testid="view-vouchers-btn">
                    View vouchers
                </Link>
            </div>
        </div>
    );
}
